// Logical pixels. Viewport scaling is applied by the UI layout, not by these text styles.
export const TEXT_SIZES = {
    tiny: 8,
    small: 12,
    medium: 14,
    normal: 16,
    large: 20,
} as const

export type TextSize = keyof typeof TEXT_SIZES

export interface TypographyStyle {
    fontSize: number
    color?: number
    bold?: boolean
    resolutionScale?: number
}

export const DEFAULT_TEXT_STYLE: TypographyStyle = { fontSize: TEXT_SIZES.medium }
export const DEFAULT_FONT_FAMILY = "Verdana, Geneva, sans-serif"
export const DISABLED_TEXT_COLOR = 0x808080

// Semantic roles for interface text, world labels, speech, and entity feedback.
export const TEXT_ROLES = {
    caption: { fontSize: TEXT_SIZES.small },
    label: { fontSize: TEXT_SIZES.medium },
    body: { fontSize: TEXT_SIZES.normal },
    title: { fontSize: TEXT_SIZES.large, bold: true },
    heading: { fontSize: 30, bold: true },
    display: { fontSize: 40, bold: true },
    // Dense HUD panels use the same hierarchy at a smaller logical scale.
    overline: { fontSize: TEXT_SIZES.tiny },
    compactCaption: { fontSize: 9 },
    compactLabel: { fontSize: 11 },
    compactBody: { fontSize: TEXT_SIZES.small },
    compactTitle: { fontSize: TEXT_SIZES.normal, bold: true },
    compactHeading: { fontSize: TEXT_SIZES.large, bold: true },
    compactDisplay: { fontSize: 30, bold: true },
    // World text retains its screen footprint and fixed speech rasterization under PiP rendering.
    entityName: { fontSize: TEXT_SIZES.small },
    entityLabel: { fontSize: TEXT_SIZES.tiny },
    speech: { fontSize: TEXT_SIZES.small, color: 0xffffff, resolutionScale: 2 },
    effect: { fontSize: 10, color: 0x00ee00 },
    damage: { fontSize: 15, color: 0xff0000, bold: true },
    entityImportant: { fontSize: 15, bold: true },
} as const

export type TextRole = keyof typeof TEXT_ROLES

// Compatibility presets preserve existing server messages and UI sizes.
export const TEXT_VARIANTS = {
    ScoreFont: DEFAULT_TEXT_STYLE,
    NameFont: DEFAULT_TEXT_STYLE,
    BannerFont: { fontSize: 20, color: 0xffffff },
    LogoFont: { fontSize: 40, color: 0xffffff },
    RedChatFont: { fontSize: 10, color: 0xffa500 },
    SmallFont: { fontSize: 10 },
    AlertFont: { fontSize: 30, color: 0xff0000 },
    DamageFont: TEXT_ROLES.damage,
    EntityImportantFont: TEXT_ROLES.entityImportant,
    ChatFont: TEXT_ROLES.effect,
    BubbleChatFont: TEXT_ROLES.speech,
    "Normal-Green-Bold": { fontSize: 15, color: 0x00ff00 },
    ImportantFont: { fontSize: 30, color: 0xffffff },
    GiganticFont: { fontSize: 100, color: 0xffffff },
} as const

export type TextVariant = keyof typeof TEXT_VARIANTS

// Server-authored text uses strings. Resolve only own preset names, with a default fallback.
export const resolveLegacyTextVariant = (name?: string): TextVariant =>
    Object.prototype.hasOwnProperty.call(TEXT_VARIANTS, name) ? name as TextVariant : "ScoreFont"
